import type {BrowserVideoConfig, BrowserH264Packet} from './browserWire';

type Callbacks = {
  /** Transfers ownership of a current frame to the caller. */
  onFrame(frame: VideoFrame): void;
  onRecovery(): void;
  onUnavailable(): void;
};

// Keep hardware work small while absorbing short delivery bursts without dropping references.
const MAX_PENDING_FRAMES = 8, MAX_PENDING_BYTES = 16 * 1024 * 1024, MAX_PENDING_AGE_MS = 150;

/** AVC deltas form a dependency chain. A real gap or bounded pending overflow invalidates
 * that entire chain; only a new configuration/generation and IDR can resume it. */
export function createBrowserVideoDecoder(callbacks: Callbacks, sequenceSpace: 'shared' | 'per-kind' = 'shared') {
  let decoder: VideoDecoder | null = null;
  let epoch = 0;
  let disposed = false;
  let state: 'unconfigured' | 'probing' | 'waiting-key' | 'decoding' | 'recovering' | 'unavailable' = 'unconfigured';
  let generation = '';
  let width = 0, height = 0, lastSequence = 0, lastTimestamp = -1;
  let pending: {packet: BrowserH264Packet; receivedAt: number}[] = [];
  let pendingBytes = 0;
  let pendingExpiry: ReturnType<typeof setTimeout> | null = null;
  const supported = new Set<string>();

  function close(): void {
    const previous = decoder;
    decoder = null;
    if (previous) previous.ondequeue = null;
    if (previous && previous.state !== 'closed') previous.close();
  }
  function reset(): void {
    epoch++;
    state = 'unconfigured';
    pending = [];
    pendingBytes = 0;
    if (pendingExpiry !== null) clearTimeout(pendingExpiry);
    pendingExpiry = null;
    close();
  }
  function recover(): void {
    if (disposed || state === 'recovering' || state === 'unavailable' || state === 'unconfigured') return;
    reset();
    state = 'recovering';
    callbacks.onRecovery();
  }
  function unavailable(): void {
    if (disposed || state === 'unavailable') return;
    reset();
    state = 'unavailable';
    callbacks.onUnavailable();
  }
  function scheduleExpiry(): void {
    if (pendingExpiry !== null) clearTimeout(pendingExpiry);
    pendingExpiry = null;
    if (!pending.length) return;
    const ownEpoch = epoch;
    pendingExpiry = setTimeout(() => {
      pendingExpiry = null;
      if (ownEpoch === epoch && pending.length) recover();
    }, Math.max(0, MAX_PENDING_AGE_MS - (performance.now() - pending[0].receivedAt)));
  }
  function drain(): void {
    if (!decoder || state !== 'decoding') return;
    if (decoder.decodeQueueSize > 2 || (pending.length && performance.now() - pending[0].receivedAt >= MAX_PENDING_AGE_MS)) {
      recover(); return;
    }
    while (pending.length && decoder && decoder.decodeQueueSize < 2) {
      const {packet} = pending.shift()!;
      pendingBytes -= packet.payload.byteLength;
      try {
        decoder.decode(new EncodedVideoChunk({
          type: packet.key_frame ? 'key' : 'delta',
          timestamp: packet.timestamp_us,
          data: packet.payload,
        }));
      } catch {recover(); return;}
    }
    scheduleExpiry();
  }
  function push(packet: BrowserH264Packet): void {
    if (disposed || packet.generation !== generation || packet.width !== width || packet.height !== height) return;
    if (state !== 'probing' && state !== 'waiting-key' && state !== 'decoding') return;
    if (lastTimestamp < 0 && !packet.key_frame) return;
    if (packet.sequence <= lastSequence || packet.timestamp_us <= lastTimestamp) return;
    // A dequeue task may still be queued when a new packet arrives; use available capacity first.
    if (state === 'decoding') {drain(); if (!decoder) return;}
    if ((lastTimestamp >= 0 && packet.sequence !== lastSequence + 1) ||
        pending.length >= MAX_PENDING_FRAMES || pendingBytes + packet.payload.byteLength > MAX_PENDING_BYTES ||
        (pending.length && performance.now() - pending[0].receivedAt >= MAX_PENDING_AGE_MS)) {
      recover();
      return;
    }
    lastSequence = packet.sequence;
    lastTimestamp = packet.timestamp_us;
    pending.push({packet, receivedAt: performance.now()});
    pendingBytes += packet.payload.byteLength;
    if (state === 'probing') scheduleExpiry();
    else {state = 'decoding'; drain();}
  }
  async function configure(packet: BrowserVideoConfig): Promise<boolean> {
    if (disposed || packet.generation === generation) return false;
    reset();
    generation = packet.generation;
    width = packet.width;
    height = packet.height;
    // Creator config packets have their own counter; frame replay is fenced by the creator gate.
    lastSequence = sequenceSpace === 'per-kind' ? 0 : packet.sequence;
    lastTimestamp = -1;
    state = 'probing';
    const ownEpoch = epoch;
    const current = (): boolean => !disposed && ownEpoch === epoch;
    const config: VideoDecoderConfig = {
      codec: packet.codec,
      description: packet.payload,
      codedWidth: width,
      codedHeight: height,
      hardwareAcceleration: 'prefer-hardware',
      optimizeForLatency: true,
    };
    const signature = `${width}:${height}:${packet.codec}:${Array.from(packet.payload).join(',')}`;
    try {
      if (typeof VideoDecoder === 'undefined') {unavailable(); return false;}
      if (!supported.has(signature)) {
        const support = await VideoDecoder.isConfigSupported(config);
        if (!current()) return false;
        if (!support.supported) {unavailable(); return false;}
        // Bound the support cache across navigation/resolution changes.
        if (supported.size >= 8) supported.clear();
        supported.add(signature);
      }
      if (!current()) return false;
      const active = new VideoDecoder({
        output(frame) {
          if (!current() || decoder !== active) {frame.close(); return;}
          if (frame.displayWidth !== width || frame.displayHeight !== height) {
            frame.close(); recover(); return;
          }
          callbacks.onFrame(frame);
        },
        error() {if (current()) recover();},
      });
      decoder = active;
      active.ondequeue = () => {if (current() && decoder === active) drain();};
      active.configure(config);
      state = pending.length ? 'decoding' : 'waiting-key';
      drain();
      return current();
    } catch {
      if (current()) unavailable();
      return false;
    }
  }
  return {configure, push, reset, dispose(): void {if (!disposed) {reset(); disposed = true; supported.clear();}}};
}
